'use client'

import { AuthClient } from '@/src/auth/AuthClient'
import type { AuthContextValue, AuthUser } from '@/types/auth'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    /**
     * Synchronizes client auth state with the backend session endpoint.
     */
    const syncSession = useCallback(async () => {
        const session = await AuthClient.getSession()
        setUser(session?.user ?? null)
    }, [])

    /**
     * Fetches the current session and updates auth state with a loading transition.
     */
    const refreshSession = useCallback(async () => {
        setIsLoading(true)
        try {
            await syncSession()
        } finally {
            setIsLoading(false)
        }
    }, [syncSession])

    useEffect(() => {
        void refreshSession()
    }, [refreshSession])

    /**
     * Authenticates a user via backend API and refreshes the active session state.
     */
    const login = async (email: string, _password: string): Promise<boolean> => {
        setIsLoading(true)
        try {
            const loginResult = await AuthClient.login(email, _password)
            if (!loginResult.ok) {
                throw new AuthRequestError(loginResult.error || 'Login failed.', loginResult.status ?? 500)
            }

            await syncSession()
            return true
        } catch (error) {
            throw toAuthRequestError(error, 'Login failed.')
        } finally {
            setIsLoading(false)
        }
    }

    // TODO CODEX
    // Registration is intentionally disabled and users are created manually in the database.
    // const register = async (email: string, _password: string, companyName: string): Promise<boolean> => {
    //     await new Promise((resolve) => setTimeout(resolve, 800))
    //
    //     const mockUser: AuthUser = {
    //         id: Date.now().toString(),
    //         email,
    //         companyName,
    //     }
    //
    //     setUser(mockUser)
    //     localStorage.setItem('companyUser', JSON.stringify(mockUser))
    //     return true
    // }

    /**
     * Invalidates the active backend session and clears local auth state.
     */
    const logout = async () => {
        setIsLoading(true)
        try {
            await AuthClient.logout()
            setUser(null)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <AuthContext.Provider value={{ user, isLoading, login, refreshSession, logout }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

/**
 * Represents an auth request failure with the originating HTTP status code.
 */
export class AuthRequestError extends Error {
    constructor(
        message: string,
        public readonly status: number,
    ) {
        super(message)
        this.name = 'AuthRequestError'
    }
}

/**
 * Normalizes unknown HTTP errors into a stable auth request error type.
 */
const toAuthRequestError = (error: unknown, fallbackMessage: string): AuthRequestError => {
    if (error instanceof AuthRequestError) {
        return error
    }

    return new AuthRequestError(fallbackMessage, 500)
}
